import { Injectable } from '@angular/core';
import {
    IEquity,
    IFinancialLineItemComponent,
    IFreeTable,
    IMovementScheduleComponent,
    IMovementScheduleFli,
    INotesComponent,
    INotesInputComponent,
    INotesSection,
    INotesSelectComponent,
    ISuperTableComponent,
    Notes,
} from '@modeso/types__aumico-lib-annualreport-be';
import { ComponentType, IRichText } from '@modeso/types__aumico-lib-notes-be';
import { IFLIForNotes } from '../interfaces/IFLIForNotes.interface';
import { MappingItemsSelectionNode } from '../interfaces/mapping-items-selection-node.interface';
import { INotesAccordionComponent } from '../interfaces/notes-accordion-component.interface';
import { INotesAccordion } from '../interfaces/notes-accordion.interface';
import { ComponentsAutoFillHelper } from './componentsAutoFill.helper';
import { MovementScheduleHelper } from './movementSchedule.helper';
import { findFlisById } from './notesCopy.helper';
import { SuperTableHelper } from './super-table.helper';

@Injectable({ providedIn: 'root' })
export class NotesAutoFillHelper {
    private flis: IFLIForNotes[] = [];
    private movementScheduleFlis: IMovementScheduleFli[] = [];

    constructor(
        private componentsAutoFillHelper: ComponentsAutoFillHelper,
        private movementScheduleHelper: MovementScheduleHelper,
        private superTableHelper: SuperTableHelper,
    ) { }

    autofillAccordions(
        oldAccordions: INotesSection[],
        flis: IFLIForNotes[],
        allMappingItemsSelectionTree: MappingItemsSelectionNode,
    ): INotesAccordion[] {
        this.flis = [...flis];
        this.movementScheduleFlis = this.movementScheduleHelper.prepareMovementScheduleFlis(flis);
        const autoFilledAccordionData: INotesAccordion[] = [];

        if (oldAccordions == null) {
            return autoFilledAccordionData;
        }

        oldAccordions.forEach((accordion: INotesSection) => {
            if (!accordion) {
                return;
            }
            const newAutoFillAccordion = { ...accordion, referenceFlis: findFlisById(accordion.referenceFlis || [], flis) };
            newAutoFillAccordion.data = this.autofillAccordion(accordion.data, allMappingItemsSelectionTree, flis);
            autoFilledAccordionData.push(newAutoFillAccordion);
        });

        return autoFilledAccordionData;
    }

    private autofillAccordion(
        oldAccordionData: INotesComponent[],
        allMappingItemsSelectionTree: MappingItemsSelectionNode,
        flis: IFLIForNotes[],
    ): INotesAccordionComponent[] {
        if (oldAccordionData == null || oldAccordionData.length === 0) {
            return oldAccordionData;
        }
        const autoFilledAccordionData: INotesAccordionComponent[] = [];
        oldAccordionData.forEach((component: INotesComponent) => {
            if (!component) {
                return;
            }
            const componentName = component.componentName;
            const componentId = component.componentId;
            const oldData: Notes = component.data;
            const autoFilledData: Notes = this.autofillComponent(componentName, oldData, allMappingItemsSelectionTree, flis);
            const autofilledComponent: INotesAccordionComponent = {
                componentName,
                data: autoFilledData,
                componentId,
                referenceFlis: findFlisById(component.referenceFlis || [], flis),
                addNumbering: component.addNumbering,
                componentTitle: component.componentTitle,
                hasFootnotes: component.hasFootnotes,
                isHidden: component.isHidden,
            };

            if (component.componentName === 'Super Table') {
                autofilledComponent.previousYearData = component.data;
            }
            autoFilledAccordionData.push(autofilledComponent);
        });

        return autoFilledAccordionData;
    }

    private autofillComponent(
        componentName: ComponentType,
        oldComponentData: Notes,
        allMappingItemsSelectionTree: MappingItemsSelectionNode,
        flis: IFLIForNotes[]
    ): Notes {
        switch (componentName) {
            case ComponentType.RICH_TEXT:
                return this.componentsAutoFillHelper.autofillRichText(oldComponentData as IRichText[]);

            case ComponentType.EQUITY:
                return this.componentsAutoFillHelper.autofillEquity(oldComponentData as IEquity);

            case ComponentType.FINANCIAL_LINE_ITEM:
                return this.componentsAutoFillHelper.autofillFinancialLineItem(
                    oldComponentData as IFinancialLineItemComponent,
                    this.flis,
                );

            case ComponentType.FREE_TABLE_WITH_AMOUNTS:
                return this.componentsAutoFillHelper.autofillFreeTable(oldComponentData as IFreeTable, flis);

            case ComponentType.SELECT:
                return this.componentsAutoFillHelper.autofillSelect(oldComponentData as INotesSelectComponent);

            case ComponentType.INPUT:
                return this.componentsAutoFillHelper.autofillInput(oldComponentData as INotesInputComponent);

            case ComponentType.MOVEMENT_SCHEDULE:
                return this.componentsAutoFillHelper.autoFillMovementSchedule(
                    oldComponentData as IMovementScheduleComponent,
                    this.movementScheduleFlis,
                );
            case ComponentType.SUPER_TABLE:
                return this.componentsAutoFillHelper.autoFillSuperTable(
                    oldComponentData as ISuperTableComponent,
                    allMappingItemsSelectionTree,
                    flis
                );

            default:
                throw new Error(`This component ${componentName} is not supported`);
        }
    }
}
